/**
 * Hiring-demand board — the frozen name column, and why every row shares it.
 *
 * THE COLUMNS INSIDE THE NAME COLUMN ARE FIXED POSITIONS, and the tree's indent
 * is padding INSIDE the first of them. That is the whole point: a role under a
 * team, a role sitting loose on its unit, a person already in the seat and the
 * draft row you are typing into all sit at different depths, and every one of
 * them must put its IC level in the same place. They used to lay themselves out
 * three different ways — two grids with different rails and paddings, and the
 * composer as a flex run — so nothing lined up with anything and reading across
 * a row meant re-finding each value.
 *
 * THE COLUMN ORDER IS THE SAME QUESTION ASKED OF EVERY ROW, which is what lets
 * a lead read down a unit instead of re-finding each value per line (Theresa,
 * Sep 2026 — the order below is hers):
 *
 *   1  what        the role title, or the person's job title
 *   2  level       IC0–M7
 *   3  FTE         what the seat is worth
 *   4  who         the person's name, or new hire / backfill. WHOSE seat a
 *                  backfill replaces is on the chip's tooltip: it is worth
 *                  having and it is not worth 170 pixels on every row, which is
 *                  what it cost the role titles
 *   5  standing    hiring status for a role; external or on leave for a person,
 *                  and nothing at all for an ordinary internal colleague — an
 *                  "internal" badge on everybody marks nothing
 *   6  actions     approve, remove, move to a team, raise a backfill
 *
 * Note what moved and why: the person's NAME is no longer the first thing on
 * their row. A role's first column is what the seat is for, and a person's row
 * has to answer the same question in the same place or the two do not line up.
 */

/**
 * Six fixed tracks. Column 1 takes whatever the name column has left — and it
 * has to be enough to READ a role title, which is the one thing every row is
 * about ("Senior Talent Acquisition Manager" is 33 characters). Every other
 * track is sized to its own content and no more, so the slack goes to column 1.
 */
export const NAME_SUB = 'minmax(0,1fr) 48px 84px 92px 96px 92px';

/** One level of the tree, in pixels of padding inside column 1. */
export const INDENT = 16;

/** Department 0 · unit 1 · team 2 · a role or a person 3. */
export const indentPx = (depth: number) => 10 + Math.max(0, depth) * INDENT;

/**
 * The frozen first column of any row.
 *
 * ⚠️ The indent goes INSIDE column 1, never on the grid. Padding the container
 * would move every column right by the depth — which is exactly the bug this
 * file exists to end, since a role under a team is one level deeper than a role
 * on its unit and their levels must still share an x.
 *
 * Sticky, and it paints its own background: a sticky cell slides over whatever
 * is beneath it, so a transparent one shows the months through itself.
 */
export function NameCells({
  depth,
  who,
  bg = 'bg-white',
  className = '',
  children,
}: {
  depth: number;
  /** Column 1 — the role title, or a person's name. Indented for you. */
  who: React.ReactNode;
  bg?: string;
  className?: string;
  /** Columns 2–6, in order: level, FTE, who, standing, actions. */
  children: React.ReactNode;
}) {
  return (
    <div
      className={`sticky left-0 z-[2] grid min-w-0 items-center gap-1.5 border-r border-gray-200 pr-2 ${bg} ${className}`}
      style={{ gridTemplateColumns: NAME_SUB }}
    >
      <div
        className="flex min-w-0 items-center gap-1.5"
        style={{ paddingLeft: indentPx(depth) }}
      >
        {who}
      </div>
      {children}
    </div>
  );
}

/** Columns 2–5: small, and the same shape whatever fills them. */
export const CELL = 'flex min-w-0 items-center gap-1 text-[11px] leading-4';

/** Column 6. Right-aligned so the controls line up down the board. */
export const ACTIONS = 'flex min-w-0 items-center justify-end gap-1';
